// ResultsRepository.ts
import { inject, injectable } from "inversify";
import axios from "axios";
import { TYPES } from "@utils/inversifyJS/types";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import type { IAuthenticatedRequestHandlerService } from "@application/contracts/services";
import { IResultsRepository } from "@domain/repositories/IResultsRepository";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { FileMetadata } from "@domain/schemas/BI";

@injectable()
export class ResultsRepository implements IResultsRepository {
  @inject(TYPES.IAuthenticatedRequestHandlerService)
  private _requestHandlerService!: IAuthenticatedRequestHandlerService;

  private buildUrl(baseUrl: string, selectedFiles?: string[]): string {
    const parameters = new URLSearchParams();
    const filteredFiles = selectedFiles?.filter((file) => file.trim().length > 0) ?? [];
    for (const file of filteredFiles) {
      parameters.append("selectedFiles", file);
    }

    const queryString = parameters.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }

  async downloadFromS3(jobId: string, selectedFiles?: string[]): Promise<string> {
    const url = this.buildUrl(
      `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.resultsS3Download}`.replace("{job_id}", () => jobId),
      selectedFiles,
    );
    return this._requestHandlerService.handleRequestAsync<string>(() => axios.get<string>(url));
  }

  async downloadStepArtifacts(jobId: string, jobPipelineStepId: string, selectedFiles?: string[]): Promise<string> {
    const url = this.buildUrl(
      `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.stepResultsDownload}`.replace("{job_id}", () => jobId).replace("{job_pipeline_step_id}", () => jobPipelineStepId),
      selectedFiles,
    );
    return this._requestHandlerService.handleRequestAsync<string>(() => axios.get<string>(url));
  }

  async downloadBatchArtifacts(jobId: string, jobPipelineStepId: string, batchId: string, selectedFiles?: string[]): Promise<string> {
    const url = this.buildUrl(
      `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.batchArtifactsDownload}`
        .replace("{job_id}", () => jobId)
        .replace("{job_pipeline_step_id}", () => jobPipelineStepId)
        .replace("{batch_id}", () => batchId),
      selectedFiles,
    );
    return this._requestHandlerService.handleRequestAsync<string>(() => axios.get<string>(url));
  }

  async listJobResultFiles(jobId: string): Promise<FileMetadata[]> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.resultsS3Files}`.replace("{job_id}", () => jobId);
    return this._requestHandlerService.handleRequestAsync<FileMetadata[]>(() => axios.get<FileMetadata[]>(url));
  }

  async listStepArtifactFiles(jobId: string, jobPipelineStepId: string): Promise<FileMetadata[]> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.stepResultsFiles}`.replace("{job_id}", () => jobId).replace("{job_pipeline_step_id}", () => jobPipelineStepId);
    return this._requestHandlerService.handleRequestAsync<FileMetadata[]>(() => axios.get<FileMetadata[]>(url));
  }

  async listBatchArtifactFiles(jobId: string, jobPipelineStepId: string, batchId: string): Promise<FileMetadata[]> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.batchArtifactsFiles}`
      .replace("{job_id}", () => jobId)
      .replace("{job_pipeline_step_id}", () => jobPipelineStepId)
      .replace("{batch_id}", () => batchId);
    return this._requestHandlerService.handleRequestAsync<FileMetadata[]>(() => axios.get<FileMetadata[]>(url));
  }
}
